import React, { useMemo, useState } from "react";
import { CheckBoxInput } from "../../../Buttons";
import useChecked from "../../../../../hooks/useChecked";
import { ButtonSecondary, InitialData } from "../../../../globals";
import TableUi from "../../../../Layout/TableUI";
import useFetch from "../../../../../hooks/useFetch";
import {
  cancelDeclarationTaxFree,
  createTaxFree,
  submitPaymentTaxFree,
  qrcodeTaxFree,
} from "../../../../../redux/Actions/taxFreeAction";
import { useSelector } from "react-redux";
import { have_product_reference } from "../../../../../helpers/OrdersFunctionsGlobal";
import { Tooltip } from "antd";
import { FaExclamationCircle } from "react-icons/fa";
import { returnEuroString } from "../../../../../helpers/Utils";
import { IconQrcode } from "../../../../Layout/Icons";

export default function OrderTaxFreeInitial({ order_id }) {
  const { loading, value, error, setReFetch } = useFetch(
    `/api/v1/orders/${order_id}/tax_free`
  );

  if (error || (loading && !value))
    return <InitialData error={error} loading={loading} value={value} />;

  return (
    <OrderTaxFree value={value} order_id={order_id} setReFetch={setReFetch} />
  );
}

function OrderTaxFree({ value, order_id, setReFetch }) {
  const { invoices, order_rows } = useSelector((state) => state.orders);

  const rebuildOrderRows = useMemo(
    () =>
      order_rows
        .filter((order_row) =>
          have_product_reference(order_row.product_reference)
        )
        .map((order_row) => {
          const tax_free_products = value.tax_free_products.filter(
            (d) => d.order_row_id === order_row.id
          );
          return {
            id: order_row.id,
            product_name: order_row.product_name,
            product_reference: order_row.product_reference,
            confirmed_quantity: tax_free_products.reduce(
              (prev, cur) => prev + cur.confirmed_quantity,
              0
            ),
            quantity: tax_free_products.reduce(
              (prev, cur) => prev + cur.quantity,
              0
            ),
            disabled: !invoices.some((invoice) =>
              invoice.order_invoice_order_rows.some(
                (order_invoice_order_row) =>
                  order_invoice_order_row.order_row_id == order_row.id
              )
            ),
          };
        }),
    [order_rows, value?.tax_free_products]
  );

  return (
    <div className="col-12 py-4 bg-silver">
      <Form
        value={value}
        order_rows={rebuildOrderRows}
        order_id={order_id}
        setReFetch={setReFetch}
      />
      <History value={value} />
    </div>
  );
}

const Form = ({ value, order_rows, order_id, setReFetch }) => {
  const actions = useMemo(() => {
    const initialActions = [];
    // 1: resubmit
    // 2: cancel
    // 3: vat refund
    if (
      !value.state ||
      ["REJECTED", "ACCEPTED_INCORRECT", "ACCEPTED_CORRECT"].includes(
        value.state
      )
    )
      initialActions.push(1);

    if (["ACCEPTED_INCORRECT", "ACCEPTED_CORRECT"].includes(value.state))
      initialActions.push(2);
    if (["CUSTOMS_DECLARED"].includes(value.state)) initialActions.push(3);

    return initialActions;
  }, [value.state]);

  const [loadingApi, setLoadingApi] = useState(false);
  const { onChangeChecked, checkedIncluded, checked, checkedAll, setChecked } =
    useChecked([]);

  const disabledForm = useMemo(() => {
    return checked.length == 0;
  }, [checked.length]);

  const onSubmit = async () => {
    try {
      setLoadingApi(true);
      const { success } = await createTaxFree({
        order_id,
        order_row_ids: checked,
      });
      setReFetch();
      console.log({ success });
    } finally {
      setLoadingApi(false);
    }
  };

  const checkedAllVal = checkedAll(
    order_rows.filter((d) => !d.disabled).map((d) => d.id)
  );
  return (
    <div>
      <div
        className="my-1"
        style={{
          borderBottom: "1px solid #DEE2E6",
        }}
      >
        <CheckBoxInput
          checked={checkedAllVal}
          text={<div className="text-sm font-medium">Products</div>}
          onChange={() =>
            setChecked(
              checkedAllVal
                ? []
                : order_rows.filter((d) => !d.disabled).map((d) => d.id)
            )
          }
        />
      </div>
      {order_rows?.map(
        (
          {
            product_reference,
            product_name,
            id,
            disabled,
            confirmed_quantity,
            quantity,
          },
          idx
        ) => (
          <div
            className="my-1"
            key={id}
            style={{
              borderBottom:
                order_rows?.length !== idx + 1 ? "1px solid #DEE2E6" : "none",
            }}
          >
            <CheckBoxInput
              disabled={disabled}
              checked={checkedIncluded(id)}
              text={
                <div className="text-sm">
                  {disabled && (
                    <Tooltip
                      placement="top"
                      title="An invoice hasn't been issued"
                    >
                      <FaExclamationCircle className="text-sm mr-2" />
                    </Tooltip>
                  )}
                  ({product_reference}) {product_name}{" "}
                  <span className="text-danger">
                    (Confirmed: {confirmed_quantity} / Quantity: {quantity})
                  </span>
                </div>
              }
              onChange={() => {
                onChangeChecked(id);
              }}
            />
          </div>
        )
      )}
      {value.number && (
        <div className="font-medium text-sm">
          Declaration ID: {value.number}
          <IconQrcode
                  onClick={async () => {
                    await qrcodeTaxFree(order_id);
                  }}
                >
          </IconQrcode>
        </div>
        
      )}
      {+value.total_refund > 0 && (
        <div className="font-medium text-sm">
          Total refund: {returnEuroString(value.total_refund)}
        </div>
      )}
      <div className="d-flex align-items-center justify-content-end my-3">
        {actions.length > 0 && (
          <div className="d-flex justify-content-end">
            {actions.includes(2) && (
              <div
                style={{
                  width: 120,
                }}
              >
                <ButtonSecondary
                  outline={true}
                  onClick={async () => {
                    await cancelDeclarationTaxFree(order_id);
                    setReFetch();
                  }}
                >
                  Cancel
                </ButtonSecondary>
              </div>
            )}
            {actions.includes(3) && (
              <div
                className="ml-2"
                style={{
                  width: 120,
                }}
              >
                <ButtonSecondary
                  onClick={async () => {
                    await submitPaymentTaxFree(order_id);
                    setReFetch();
                  }}
                >
                  VAT refunded
                </ButtonSecondary>
              </div>
            )}
            {actions.includes(1) && (
              <div
                className="ml-2"
                style={{
                  width: 140,
                }}
              >
                <ButtonSecondary
                  loading={loadingApi}
                  onClick={onSubmit}
                  disabled={disabledForm}
                >
                  {[null, "REJECTED"].includes(value.state)
                    ? "Create declaration"
                    : "Update declaration"}
                </ButtonSecondary>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const History = ({ value }) => {
  return (
    <div>
      <TableUi
        className={"table-borders"}
        hoverable={false}
        ths={["Date", "Action", "Response"]}
      >
        {value?.tax_free_events
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .map((e, idx) => {
            return (
              <TableUi.tr className={`tableColumm`} key={`${e.id || 0}_${idx}`}>
                <TableUi.td className={"p-left text-xs"}>
                  <div>{e.created_at.substring(0, 10)}</div>
                  <div className="text-silver">
                    {e.created_at.substring(11, 16)}
                  </div>
                </TableUi.td>
                <TableUi.td className={"text-xs"}>{e.status}</TableUi.td>
                <TableUi.td className={"text-xs"}>
                  <div className="d-flex align-items-center">
                    <div
                      className={`text-xs text-${
                        e.event_type == "error"
                          ? "danger"
                          : e.event_type == "info"
                          ? "warning"
                          : "success"
                      }`}
                    >
                      {e.event_type == "error"
                        ? "Rejected"
                        : e.event_type == "info"
                        ? "Waiting for approval"
                        : "Approved"}
                    </div>

                    {!!e.message && (
                      <Tooltip
                        placement="rightBottom"
                        overlayClassName="tooltip-white"
                        title={
                          <div className="px-2 py-1">
                            <h3 className="text-xs font-semi-bold text-black mb-1">
                              Error message:
                            </h3>
                            <p className="text-xs text-black mb-0">
                              {e.message}
                            </p>
                          </div>
                        }
                      >
                        <FaExclamationCircle
                          className="text-danger ml-1"
                          style={{
                            width: 20,
                            height: 20,
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </TableUi.td>
              </TableUi.tr>
            );
          })}
      </TableUi>
    </div>
  );
};
